// src/components/Obsazenost.tsx
import React, { useEffect, useState, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { cs } from 'date-fns/locale';
import ical from 'ical.js';
import { useTranslation } from 'react-i18next';
import { fetchIcalAvailability } from '../services/api';

const Obsazenost: React.FC = () => {
  const { t } = useTranslation();
  const [reservedDates, setReservedDates] = useState<Date[]>([]);
  const [error, setError] = useState<string>('');
  const [monthsToShow, setMonthsToShow] = useState<number>(() =>
    typeof window !== 'undefined' && window.innerWidth < 640 ? 1 : 2
  );

  useEffect(() => {
    const loadAvailability = async () => {
      try {
        const icalData = await fetchIcalAvailability();
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const jcalData = ical.parse(icalData);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        const vcalendar = new ical.Component(jcalData);
        const vevents = vcalendar.getAllSubcomponents('vevent');

        const allDates: Date[] = [];

        vevents.forEach(e => {
          const event = new ical.Event(e);
          const start = event.startDate.toJSDate();
          const end = event.endDate.toJSDate();

          // Vytvoříme nový objekt pro každý den bez mutace původního `d`
          const current = new Date(start.getFullYear(), start.getMonth(), start.getDate());
          const checkout = new Date(end.getFullYear(), end.getMonth(), end.getDate());

          // Blokujeme i den odjezdu, aby nevznikla nová rezervace ve stejný den (čas na úklid)
          while (current <= checkout) {
            allDates.push(new Date(current));
            current.setDate(current.getDate() + 1);
          }
        });

        setReservedDates(allDates);
      } catch (err) {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.error(err);
        }
        setError(t('occupancy.error'));
      }
    };

    void loadAvailability();
  }, [t]);


  const monthStart = useMemo(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window === 'undefined') return;
      setMonthsToShow(window.innerWidth < 640 ? 1 : 2);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="mt-16 text-center flex flex-col items-center">
      <h2 className="text-2xl font-bold text-[color:#4B2E2E] mb-4">{t('occupancy.title')}</h2>
      {error && <p className="text-red-500 mb-2">{error}</p>}
      <div className="w-full max-w-4xl p-4 sm:p-6">
        <DatePicker
          inline
          monthsShown={monthsToShow}
          highlightDates={reservedDates}
          dayClassName={date => {
            const reserved = reservedDates.some(d =>
              d.getFullYear() === date.getFullYear() &&
              d.getMonth() === date.getMonth() &&
              d.getDate() === date.getDate()
            );
            return reserved ? 'react-datepicker__day--highlighted-booked' : '';
          }}
          openToDate={monthStart}
          locale={cs}
          className="w-full flex justify-center"
          calendarClassName="obsazenost-datepicker"
        />
        <p className="text-sm mt-4 text-gray-600 border-t pt-3">
          {t('occupancy.legend')}
        </p>
      </div>

      <style>{`
        .obsazenost-datepicker {
          width: 100%;
          display: flex;
          justify-content: center;
          gap: 24px;
          flex-wrap: wrap;
          border: none;
          background: transparent;
        }
        .obsazenost-datepicker .react-datepicker__month-container {
          float: none;
        }
        .obsazenost-datepicker .react-datepicker__header {
          background: transparent;
          border-bottom: none;
        }
        .obsazenost-datepicker .react-datepicker__current-month {
          color: #3f2b25;
          font-weight: 700;
          font-size: 1rem;
        }
        .obsazenost-datepicker .react-datepicker__day-name {
          color: #7a6a61;
          font-weight: 600;
          width: 2.2rem;
          line-height: 2.2rem;
        }
        .obsazenost-datepicker .react-datepicker__day {
          width: 2.2rem;
          height: 2.2rem;
          line-height: 2.2rem;
          margin: 0.2rem;
          border-radius: 8px;
          color: #3f2b25;
          font-weight: 600;
        }
        .obsazenost-datepicker .react-datepicker__day:hover {
          background: #f1e9e0;
        }
        .obsazenost-datepicker .react-datepicker__day--highlighted-booked {
          background: #2f9e44;
          color: #fff;
          box-shadow: 0 6px 14px rgba(47,158,68,0.25);
        }
        .obsazenost-datepicker .react-datepicker__day--outside-month {
          visibility: hidden;
        }
        .obsazenost-datepicker .react-datepicker__navigation {
          top: 12px;
        }
        @media (max-width: 640px) {
          .obsazenost-datepicker {
            gap: 12px;
            max-width: 340px;
            margin: 0 auto;
          }
          .obsazenost-datepicker .react-datepicker__month-container {
            width: 100%;
          }
          .obsazenost-datepicker .react-datepicker {
            width: 100%;
          }
          .obsazenost-datepicker .react-datepicker__current-month {
            font-size: 0.95rem;
          }
          .obsazenost-datepicker .react-datepicker__day-name {
            width: 1.9rem;
            line-height: 1.9rem;
          }
          .obsazenost-datepicker .react-datepicker__day {
            width: 1.9rem;
            height: 1.9rem;
            line-height: 1.9rem;
            margin: 0.12rem;
            border-radius: 6px;
          }
          .obsazenost-datepicker .react-datepicker__navigation {
            top: 10px;
          }
          .obsazenost-datepicker .react-datepicker__navigation-icon::before {
            border-width: 2px 2px 0 0;
          }
        }
      `}</style>
    </div>
  );
};

export default Obsazenost;
